/**
 * WINGMAN-RESEARCH-006: MOCK REPORT GENERATOR
 * 
 * Generates canonical, schema-valid offline demo IntelligenceReport objects
 * using deterministic synthetic sources.
 * Prominently marked as: "OFFLINE DEMO — SYNTHETIC SOURCES"
 */

import type { IntelligenceReport, SpatialScope } from '../intelligence/domain';

export function generateMockCanonicalReport(
  locationOrBrief: string | { topic?: string; location?: string; scope?: SpatialScope; purposeAudience?: string; purpose?: string; reportMode?: string } = 'Spain',
  scopeArg: SpatialScope = 'NATIONAL',
  reportModeArg: string = 'STANDARD',
  purposeArg: string = 'Executive strategic analysis'
): IntelligenceReport {
  let location = 'Spain';
  let scope = scopeArg;
  let reportMode = reportModeArg;
  let purpose = purposeArg;

  if (typeof locationOrBrief === 'string') {
    location = locationOrBrief;
  } else if (locationOrBrief && typeof locationOrBrief === 'object') {
    location = locationOrBrief.topic || locationOrBrief.location || 'Active Location';
    scope = locationOrBrief.scope || scopeArg;
    purpose = locationOrBrief.purposeAudience || locationOrBrief.purpose || purposeArg;
    reportMode = locationOrBrief.reportMode || reportModeArg;
  }

  const now = new Date().toISOString();
  const reportId = `report_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

  return {
    reportId,
    title: `Intelligence Briefing: ${location}`,
    generatedAt: now,
    metadata: {
      offlineDemoNotice: 'OFFLINE DEMO — SYNTHETIC SOURCES',
      generatedAt: now,
      sourceMode: 'OFFLINE_SYNTHETIC'
    },
    spatialContext: {
      name: location,
      latitude: (location || '').toLowerCase().includes('spain') ? 40.4168 : 25.7617,
      longitude: (location || '').toLowerCase().includes('spain') ? -3.7038 : -80.1918,
      country: (location || '').toLowerCase().includes('spain') ? 'Spain' : 'United States',
      scope,
      observedAt: now
    },
    profile: {
      reportMode: reportMode as any,
      depth: 'FULL'
    },
    intent: {
      requestId: `req_${reportId}`,
      spatialContext: {
        name: location,
        latitude: 40.4168,
        longitude: -3.7038,
        scope,
        observedAt: now
      },
      profile: {
        reportMode: reportMode as any
      },
      query: purpose
    },
    executiveSummary: `OFFLINE DEMO — SYNTHETIC SOURCES. Verified executive intelligence for ${location}. Infrastructure, economic, and transportation corridors remain operational under standard flow. Grounded with deterministic mock evidence across national and regional indicators.`,
    sections: [
      {
        id: 'sec-infrastructure',
        category: 'INFRASTRUCTURE',
        title: 'Infrastructure & Logistics Corridors',
        priority: 'HIGH',
        summary: `Strategic logistics capacity across ${location} operational hubs.`,
        findings: [
          `Major multimodal transport corridors operate at 94% scheduled velocity.`,
          `High-speed rail and intermodal links maintain regular freight and passenger schedules.`,
          `Port and cargo facilities report steady supply chain throughput.`
        ],
        evidenceRefs: ['ev-infra-01', 'ev-infra-02'],
        recommendedActions: [
          `Monitor ongoing terminal upgrades during off-peak windows.`,
          `Confirm freight slots 48 hours in advance for high-volume corridors.`
        ],
        claims: [
          {
            claimId: 'claim-infra-01',
            sectionId: 'sec-infrastructure',
            text: 'Multimodal corridors maintain 94% scheduled velocity.',
            type: 'FACTUAL',
            evidenceRefs: ['ev-infra-01'],
            sourceRefs: ['src-infra-registry'],
            trustStatus: 'ASSERTABLE'
          }
        ]
      },
      {
        id: 'sec-economy',
        category: 'ECONOMY',
        title: 'Economic & Commercial Overview',
        priority: 'MEDIUM',
        summary: `Commercial activity and investment stability across regional nodes.`,
        findings: [
          `Commercial real estate and investment interest remains focused on central metropolitan zones.`,
          `Consumer index and service sector indicators show steady seasonal stability.`
        ],
        evidenceRefs: ['ev-econ-01'],
        claims: [
          {
            claimId: 'claim-econ-01',
            sectionId: 'sec-economy',
            text: 'Commercial real estate demand remains stable in urban cores.',
            type: 'FACTUAL',
            evidenceRefs: ['ev-econ-01'],
            sourceRefs: ['src-econ-bulletin'],
            trustStatus: 'ASSERTABLE'
          }
        ]
      },
      {
        id: 'sec-weather',
        category: 'WEATHER',
        title: 'Atmospheric & Climate Conditions',
        priority: 'LOW',
        summary: `Current regional meteorological conditions.`,
        findings: [
          `Predominantly clear conditions with localized maritime winds. Visibility 10+ miles.`
        ],
        evidenceRefs: ['ev-weather-01'],
        claims: [
          {
            claimId: 'claim-weather-01',
            sectionId: 'sec-weather',
            text: 'Visual meteorological conditions prevail with 10+ miles visibility.',
            type: 'FACTUAL',
            evidenceRefs: ['ev-weather-01'],
            sourceRefs: ['src-meteo-service'],
            trustStatus: 'ASSERTABLE'
          }
        ]
      }
    ],
    evidence: [
      {
        id: 'ev-infra-01',
        category: 'INFRASTRUCTURE',
        finding: 'Multimodal logistics network operating at 94% scheduled efficiency.',
        source: 'Synthetic Logistics Index',
        sourceUrl: 'https://example.com/logistics/synthetic-index',
        observedAt: now,
        relevance: { overall: 0.95 },
        freshness: 'CURRENT',
        confidence: 'HIGH',
        provenance: 'TEST',
        metadata: {
          quote_offset_start: 0,
          quote_offset_end: 67,
          sha256: '9f86d081884c7d659a2feaa0c55ad015a3bf4f1b2b0b822cd15d6c15b0f00a08'
        }
      },
      {
        id: 'ev-econ-01',
        category: 'ECONOMY',
        finding: 'Commercial real estate index steady across primary regional districts.',
        source: 'Synthetic Economic Monitor',
        sourceUrl: 'https://example.com/economy/synthetic-monitor',
        observedAt: now,
        relevance: { overall: 0.90 },
        freshness: 'CURRENT',
        confidence: 'HIGH',
        provenance: 'TEST',
        metadata: {
          quote_offset_start: 0,
          quote_offset_end: 72,
          sha256: '5e884898da28047151d0e56f8dc6292773603d0d6aabbdd62a11ef721d1542d8'
        }
      },
      {
        id: 'ev-weather-01',
        category: 'WEATHER',
        finding: 'Visual meteorological conditions with 10+ miles visibility.',
        source: 'Synthetic Meteorological Observation',
        sourceUrl: 'https://example.com/weather/synthetic-meteo',
        observedAt: now,
        relevance: { overall: 0.88 },
        freshness: 'CURRENT',
        confidence: 'HIGH',
        provenance: 'TEST',
        metadata: {
          quote_offset_start: 0,
          quote_offset_end: 60,
          sha256: '4b227777d4dd1fc61c6f884f48641d02b4d121d3fd328cb08b5531fcacdabf8a'
        }
      }
    ],
    sources: [
      {
        id: 'src-infra-registry',
        name: 'Synthetic National Infrastructure Registry',
        url: 'https://example.com/sources/infra',
        provider: 'MOCK_SYNTHETIC',
        observedAt: now
      },
      {
        id: 'src-econ-bulletin',
        name: 'Synthetic Regional Economic Bulletin',
        url: 'https://example.com/sources/econ',
        provider: 'MOCK_SYNTHETIC',
        observedAt: now
      },
      {
        id: 'src-meteo-service',
        name: 'Synthetic Atmospheric Observation Service',
        url: 'https://example.com/sources/weather',
        provider: 'MOCK_SYNTHETIC',
        observedAt: now
      }
    ],
    claims: [
      {
        claimId: 'claim-infra-01',
        sectionId: 'sec-infrastructure',
        text: 'Multimodal corridors maintain 94% scheduled velocity.',
        type: 'FACTUAL',
        evidenceRefs: ['ev-infra-01'],
        sourceRefs: ['src-infra-registry'],
        trustStatus: 'ASSERTABLE'
      },
      {
        claimId: 'claim-econ-01',
        sectionId: 'sec-economy',
        text: 'Commercial real estate demand remains stable in urban cores.',
        type: 'FACTUAL',
        evidenceRefs: ['ev-econ-01'],
        sourceRefs: ['src-econ-bulletin'],
        trustStatus: 'ASSERTABLE'
      },
      {
        claimId: 'claim-weather-01',
        sectionId: 'sec-weather',
        text: 'Visual meteorological conditions prevail with 10+ miles visibility.',
        type: 'FACTUAL',
        evidenceRefs: ['ev-weather-01'],
        sourceRefs: ['src-meteo-service'],
        trustStatus: 'ASSERTABLE'
      }
    ],
    methodology: {
      generatedAt: now,
      evidenceCount: 3,
      sourceCount: 3,
      coverageSummary: {
        INFRASTRUCTURE: 'STRONG',
        ECONOMY: 'ADEQUATE',
        WEATHER: 'ADEQUATE'
      },
      contradictionsCount: 0,
      knownGaps: [
        'OFFLINE DEMO — SYNTHETIC SOURCES: Data generated offline using deterministic test fixtures.',
        'No external paid provider calls were executed.',
        'Production routing remains disabled.'
      ]
    },
    tables: [
      {
        title: 'Operational Indicators & Strategic Metrics',
        headers: ['Sector / Metric', 'Current Value', 'Projected (2028)', 'Impact Rating'],
        rows: [
          ['Logistics Corridor Flow', '94% On-Time', '97% Target', 'HIGH'],
          ['Renewable Grid Share', '58.4%', '74.0%', 'CRITICAL'],
          ['Regional Transit Congestion', 'Low-Moderate', 'Low', 'MODERATE'],
          ['Commercial Real Estate Vacancy', '7.2%', '6.8%', 'STABLE']
        ]
      }
    ],
    technicalAppendix: {
      provenanceHash: 'sha256-e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855',
      modelIdentifier: 'gpt-researcher-canonical-offline-v1',
      executionLatencyMs: 38,
      sourceMode: 'SYNTHETIC_OFFLINE',
      byteOffsetsVerified: true,
      internalNodeId: 'node-local-offline-001'
    }
  };
}
